import { compact } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';

import { EM_DASH } from '~/common/constants/unicode';
import AutomationHubManagement from '~/components/AutomationHub/AutomationHubManagement/AutomationHubManagement';
import { HELP_CENTER_LINK } from '~/components/AutomationHub/constants';
import { buildClauseAnalyzerParams } from '~/components/AutomationHub/util';
import RenameClauseModal from '~/components/Modals/RenameClauseModal/RenameClauseModal';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { Filters_DEPRECATED } from '~/components/Shared/Filters_DEPRECATED/Filters_DEPRECATED';
import { Button } from '~/eds/src/components/Button/Button';
import { DateText } from '~/eds/src/components/DateText/DateText';
import { EmptyPage } from '~/eds/src/components/EmptyPage/EmptyPage';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { LoadingShimmer } from '~/eds/src/components/LoadingShimmer/LoadingShimmer';
import { PageLayout } from '~/eds/src/modules/PageLayout/PageLayout';
import { Table } from '~/eds/src/modules/Table/Table';
import { AutomationClauseType } from '~/enums/AutomationClauseTypes';
import { AutomationStageType } from '~/enums/AutomationStageType';
import { AutomationStatusType } from '~/enums/AutomationStatusType';
import { FeatureFlagType } from '~/enums/FeatureFlagType';
import { QueryParamType } from '~/enums/QueryParamType';
import { QueryFilters } from '~/features/advanced-search/query-filters/QueryFilters';
import {
  ALL_STATUSES,
  ALL_TYPES,
  CLAUSE_STATUS_ID,
  CLAUSE_TYPE_ID,
  filtersOptions,
} from '~/features/automation/constants';
import {
  useGetClauseStatusOption,
  useGetClauseTypeOption,
} from '~/features/automation/hooks';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useClientSubDomain } from '~/hooks/useClientSubDomain';
import { useHasFeatureFlag } from '~/hooks/useHasFeatureFlag';
import { useResolveUsers } from '~/hooks/useResolveUsers';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import searchSlice from '~/redux/slices/search';
import { useRouting } from '~/routing/useRouting';
import { redirectToPage } from '~/services/redirects';
import { ERROR, SUCCESS } from '~/types/toast.types';
import Alert from '~/ui/components/Alert/Alert';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import LoadingContainer from '~/ui/components/LoadingContainer/LoadingContainer';
import Text from '~/ui/components/Text/Text';
import { getLatestDate } from '~/utils/dates';
import { toLowercaseTrimmed } from '~/utils/strings';

const Page = () => {
  const intl = useIntl();
  const formatNumber = useFormatNumber();
  const dispatch = useDispatch();
  const { navigate } = useRouting();
  const subdomain = useClientSubDomain();

  const clause = useSelector(
    slices.automation.selectors.selectAutomationClause,
  );

  const pagination = useSelector(
    slices.automation.selectors.selectAutomationClausePagination,
  );

  const hideClauseAffordance = useFlag(FlagType.HideClauseAffordance);
  const hasAutomationSmartClauseFull = useHasFeatureFlag(
    FeatureFlagType.AutomationSmartClauseFull,
  );
  const hasAllowClauseRenaming = useHasFeatureFlag(
    FeatureFlagType.AllowClauseRenaming,
  );
  const enableQuickAiFlag = useFlag(FlagType.QuickAiFilters);
  const hasAdvancedSearch = useHasFeatureFlag(FeatureFlagType.SearchV2);
  const hasAdvancedSearchV3 = useFlag(FlagType.SearchV3);

  const CLAUSE_DEFAULT_STATUS_FILTER = [AutomationStatusType.ACTIVE];
  const CLAUSE_DEFAULT_TYPE_FILTER = hasAutomationSmartClauseFull
    ? [AutomationClauseType.QUICK_AI, AutomationClauseType.GUIDED_AI]
    : [AutomationClauseType.QUICK_AI];

  const usingQuickAiFilterOnly = clause?.typeFilter?.every(
    (f) => f === AutomationClauseType.QUICK_AI,
  );
  const usingAllTypeFilters = clause?.typeFilter?.length >= 2;
  const usingSingleStatusFilter = clause?.statusFilter?.length <= 1;

  const [showClauseLimit, setShowClauseLimit] = useState(true);
  const [renameClauseValue, setRenameClauseValue] = useState();
  const isNavigatingToDetailRef = useRef(false);

  const newClauseText = intl.formatMessage({
    id: 'AutomationPage.newClauseButton',
    defaultMessage: 'New Clause',
    description: 'Button text for creating new clause',
  });

  useEffect(() => {
    dispatch(slices.automation.actions.resetAutomationGuidedAiDataForm());
  }, []);

  // Cleanup: reset automation state unless navigating to detail page
  useEffect(() => {
    return () => {
      if (!isNavigatingToDetailRef.current) {
        dispatch(slices.automation.actions.resetAutomationStates());
      }
    };
  }, []);

  const { data: allClauses, isFetching: isFetchingAllClauses } =
    api.endpoints.getClauses.useQuery({
      ...pagination,
      clauseType: hasAutomationSmartClauseFull
        ? ALL_TYPES
        : CLAUSE_DEFAULT_TYPE_FILTER,
      status: ALL_STATUSES,
    });

  const dataExists = !!allClauses && allClauses.count > 0;

  const { data, error, isFetching } = api.endpoints.getClauses.useQuery(
    {
      ...pagination,
      clauseType: hasAutomationSmartClauseFull
        ? clause?.typeFilter
        : CLAUSE_DEFAULT_TYPE_FILTER,
      status: clause?.statusFilter,
    },
    { skip: !dataExists },
  );

  const { data: sectionsAndFolders, isFetching: isFetchingSections } =
    api.endpoints.getFilterSections.useQuery(undefined);

  const [renameClause, renameClauseResult] =
    api.endpoints.renameClause.useMutation();
  const {
    isError: isRenameClauseError,
    isSuccess: isRenameClauseSuccess,
    error: renameClauseError,
    originalArgs: renameClauseOriginalArgs,
  } = renameClauseResult;

  const choiceHasNoData = !!data && data.count <= 0;

  const clausesUsed = allClauses?.total;
  const clauseLimit = allClauses?.limit;
  const clausesLeft = clauseLimit - clausesUsed;
  const percentageUsed = clausesUsed / clauseLimit;
  const clauseVariant =
    percentageUsed >= 1 ? 'danger' : percentageUsed >= 0.8 ? 'warning' : 'info';

  const { users: resolvedUsers, isFetching: isResolvingUsers } =
    useResolveUsers({
      userIds: data?.results?.map((clause) => clause.createdBy).flat(),
    });

  useEffect(() => {
    if (error) {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'AutomationPage.failedLoadClauses',
          defaultMessage: 'Failed load Clauses. Please try again.',
          description: 'Error message when clauses fail to load',
        }),
      );
    }
  }, [error, intl]);

  useEffect(() => {
    if (isRenameClauseSuccess) {
      setRenameClauseValue('');
      showToast(
        SUCCESS,
        intl.formatMessage(
          {
            id: 'AutomationPage.renameSuccess',
            defaultMessage: '"{clauseName}" has been renamed.',
            description:
              'Success message when clause is renamed. Example: clauseName = "Termination Clause"',
          },
          {
            clauseName: renameClauseOriginalArgs.newName,
          },
        ),
      );
    }
    if (isRenameClauseError) {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'AutomationPage.renameFailed',
          defaultMessage: 'Rename failed',
          description: 'Error message when rename fails',
        }),
      );
    }
  }, [isRenameClauseSuccess, isRenameClauseError, intl]);

  const onSetFilter = (filterValues) => {
    const typeFilterValue = filterValues.find((e) => e.id === CLAUSE_TYPE_ID);

    let { values: typeFilter } = typeFilterValue ?? { values: [] };
    if (typeFilter.length <= 0) typeFilter = CLAUSE_DEFAULT_TYPE_FILTER;

    if (
      !hasAutomationSmartClauseFull ||
      typeFilter.every((f) => f === AutomationClauseType.QUICK_AI)
    ) {
      const statusFilterValue = filterValues.find(
        (e) => e.id === CLAUSE_STATUS_ID,
      );

      let { values: statusFilter } = statusFilterValue ?? { values: [] };
      if (statusFilter.length <= 0) statusFilter = CLAUSE_DEFAULT_STATUS_FILTER;

      dispatch(
        slices.automation.actions.setAutomationClause({
          ...clause,
          typeFilter,
          statusFilter,
        }),
      );
    } else {
      dispatch(
        slices.automation.actions.setAutomationClause({
          ...clause,
          typeFilter,
          statusFilter: [],
        }),
      );
    }
  };

  const onClickClause = ({ name, status, clauseId, types }) => {
    isNavigatingToDetailRef.current = true;

    dispatch(
      slices.automation.actions.setAutomationClause({
        ...clause,
        name,
        status,
        types,
      }),
    );

    let url = `/automation/detail?${QueryParamType.Clause}=${encodeURIComponent(
      name,
    )}`;

    if (hasAutomationSmartClauseFull && clauseId) {
      url = `${url}&${QueryParamType.clauseId}=${clauseId}`;
    }

    navigate(url);
  };

  const onCreateNewClause = () => {
    if (hasAutomationSmartClauseFull) {
      dispatch(
        slices.automation.actions.setAutomationManageStage(
          AutomationStageType.AutomationTypes,
        ),
      );
    } else {
      dispatch(
        slices.automation.actions.setAutomationManageStage(
          AutomationStageType.QuickAiForm,
        ),
      );
    }
  };

  const redirectToAnalyzerResults = ({ name }) => {
    const params = new URLSearchParams(
      buildClauseAnalyzerParams(name),
    ).toString();
    redirectToPage(subdomain, `analyzer/results?${params}`);
  };

  const redirectToAdvancedSearch = ({ name }) => {
    const values = [{ provision: name, text_search: [] }];
    const filters = [
      { id: uuid(), fieldId: 'clause', operatorId: 'contains_any', values },
    ];
    const fields = sectionsAndFolders?.fields;
    if (hasAdvancedSearchV3) {
      dispatch(slices.searchV3.actions.setSelectedFilters(filters));
    } else {
      dispatch(searchSlice.actions.setFilters(filters));
      dispatch(
        searchSlice.actions.setQuery({ booleanQuery: '', filters, fields }),
      );
    }
    navigate('/search');
  };

  const redirectToSearchResults = ({ name }) => {
    if (hasAdvancedSearch) {
      redirectToAdvancedSearch({ name });
    } else {
      redirectToAnalyzerResults({ name });
    }
  };
  const tooltip = intl.formatMessage({
    id: 'AutomationPage.identificationsTooltip',
    defaultMessage:
      "Displays the number of manually tagged clauses through Quick AI by users in your organization. This does not include clauses identified automatically by Evisort's standard clause models.",
    description: 'Tooltip text for identifications column header',
  });

  const identificationsTitle = (
    <Layout alignItems="center" flexDirection="row" space={1}>
      <Text>
        {intl.formatMessage({
          id: 'AutomationPage.identificationsHeader',
          defaultMessage: 'Identifications',
          description: 'Column header for identifications',
        })}
      </Text>
      <Icon icon="info" color="status-info" tooltip={tooltip} />
    </Layout>
  );

  const fields = {
    [CLAUSE_TYPE_ID]: {
      id: CLAUSE_TYPE_ID,
      label: intl.formatMessage({
        id: 'AutomationPage.typeFilter',
        defaultMessage: 'Type',
        description: 'Label for type filter field',
      }),
      type: 'enum_set',
      whitelistOperatorIds: ['contains_any'],
      overrideOperatorLabels: {
        contains_any: intl.formatMessage({
          id: 'AutomationPage.typeFilterIncludes',
          defaultMessage: 'includes',
          description: 'Label for type filter operator',
        }),
      },
      settings: {
        __testEnableSelectAll: () => false,
        options: useGetClauseTypeOption(),
      },
    },
    [CLAUSE_STATUS_ID]: {
      id: CLAUSE_STATUS_ID,
      label: intl.formatMessage({
        id: 'AutomationPage.statusFilter',
        defaultMessage: 'Status',
        description: 'Label for status filter field',
      }),
      type: 'enum_set',
      whitelistOperatorIds: ['contains_any'],
      overrideOperatorLabels: {
        contains_any: intl.formatMessage({
          id: 'AutomationPage.statusFilterIncludes',
          defaultMessage: 'includes',
          description: 'Label for status filter operator',
        }),
      },
      settings: {
        __testEnableSelectAll: () => false,
        options: useGetClauseStatusOption(),
      },
    },
  };

  const filters = compact([
    hasAutomationSmartClauseFull && {
      id: CLAUSE_TYPE_ID,
      fieldId: CLAUSE_TYPE_ID,
      operatorId: 'contains_any',
      values: clause?.typeFilter ?? [],
    },
    (!hasAutomationSmartClauseFull || usingQuickAiFilterOnly) && {
      id: CLAUSE_STATUS_ID,
      fieldId: CLAUSE_STATUS_ID,
      operatorId: 'contains_any',
      values: clause?.statusFilter ?? [],
    },
  ]);

  const columns = [
    {
      key: 'name',
      cellType: 'link',
      title: intl.formatMessage({
        id: 'AutomationPage.clauseColumn',
        defaultMessage: 'Clause',
        description: 'Column header for clause name',
      }),
      minWidth: 'xl',
      align: 'left',
      disableSortBy: true,
      mapCellProps: ({ name, status, types, customModelId }) => ({
        text: name,
        shouldTruncate: true,
        onClick: () =>
          onClickClause({ name, status, types, clauseId: customModelId }),
      }),
    },
    {
      key: 'createdBy',
      title: intl.formatMessage({
        id: 'AutomationPage.createdByColumn',
        defaultMessage: 'Created by',
        description: 'Column header for created by',
      }),
      cellType: 'users',
      minWidth: 'l',
      disableSortBy: true,
      mapCellProps: ({ createdBy }) => ({
        users: createdBy?.map((id) => resolvedUsers?.[id] ?? {}),
      }),
    },
    {
      key: 'lastIdentification',
      title: intl.formatMessage({
        id: 'AutomationPage.lastIdentificationColumn',
        defaultMessage: 'Last Identification',
        description: 'Column header for last identification',
      }),
      minWidth: 'l',
      disableSortBy: true,
      renderCell: ({ name }) => {
        const { data: d, isFetching: i } =
          api.endpoints.getIdentifications.useQuery({ name });
        if (i) return <LoadingShimmer />;

        const date = getLatestDate(
          d?.bulkTaggingLastIdentification,
          d?.smartClauseLastIdentification,
        );

        return (
          <Text variant="s-dense">
            <DateText date={date} />
          </Text>
        );
      },
    },
    {
      key: 'totalIdentifications',
      title: identificationsTitle,
      minWidth: 'l',
      disableSortBy: true,
      align: 'right',
      renderCell: ({ name }) => {
        const {
          data: d = {
            bulkTaggingTotalIdentifications: 0,
            smartClauseTotalIdentifications: 0,
          },
          isFetching: i,
          isError: isErrorIdentifications,
        } = api.endpoints.getIdentifications.useQuery({ name });
        if (i) return <LoadingShimmer />;

        const bulkTaggingTotalIdf = d.bulkTaggingTotalIdentifications;
        const smartClauseTotalIdf = d.smartClauseTotalIdentifications;
        const totalIdentifications = bulkTaggingTotalIdf + smartClauseTotalIdf;

        return (
          <Button
            isLoading={isFetchingSections}
            disabled={totalIdentifications === 0 || isErrorIdentifications}
            text={
              isErrorIdentifications
                ? intl.formatMessage({
                    id: 'AutomationPage.countNotAvailable',
                    defaultMessage: 'Count not available',
                    description: 'Message shown when count is not available',
                  })
                : totalIdentifications > 0
                  ? formatNumber(totalIdentifications)
                  : EM_DASH
            }
            tooltip={
              isErrorIdentifications
                ? intl.formatMessage({
                    id: 'AutomationPage.viewIdentificationsTooltip',
                    defaultMessage: 'Use View Identifications to see documents',
                    description: 'Tooltip shown when count is not available',
                  })
                : undefined
            }
            variant={isErrorIdentifications ? 'tertiary' : 'action'}
            onClick={() => redirectToSearchResults({ name })}
          />
        );
      },
    },
    ...(enableQuickAiFlag
      ? [
          {
            key: 'filters',
            title: intl.formatMessage({
              id: 'AutomationPage.filtersColumn',
              defaultMessage: 'Filters',
              description: 'Column header for filters',
            }),
            disableSortBy: true,
            renderCell: ({ name }) => {
              const {
                data: identifications,
                isFetching: isFetchingIdentifications,
              } = api.endpoints.getIdentifications.useQuery({
                name,
              });

              if (isFetchingIdentifications) return <LoadingShimmer />;

              const query = identifications?.filters
                ? identifications.filters
                : undefined;

              return (
                <QueryFilters
                  onQueryChange={() => {}}
                  placeholder="–"
                  query={query ?? []}
                  readOnly
                />
              );
            },
          },
        ]
      : []),
  ];

  const rowActions = hasAllowClauseRenaming
    ? [
        {
          label: intl.formatMessage({
            id: 'AutomationPage.renameAction',
            defaultMessage: 'Rename',
            description: 'Label for rename action',
          }),
          onClick: (row) => {
            setRenameClauseValue(row?.name);
          },
        },
      ]
    : [];

  if (
    hasAutomationSmartClauseFull &&
    !isFetching &&
    dataExists &&
    choiceHasNoData
  ) {
    if (usingAllTypeFilters) {
      if (usingSingleStatusFilter) {
        showToast(
          SUCCESS,
          intl.formatMessage(
            {
              id: 'AutomationPage.resetStatusFiltersMessage1',
              defaultMessage:
                'Because you have no {status} clauses, we reset your filters to show you all clauses.',
              description:
                'Message when status filters are reset. Example: status = "active"',
            },
            {
              status: toLowercaseTrimmed(clause?.statusFilter?.[0] ?? 'active'),
            },
          ),
        );

        dispatch(
          slices.automation.setAutomationClause({
            ...clause,
            statusFilter: ALL_STATUSES,
          }),
        );
      }
    } else {
      if (usingQuickAiFilterOnly) {
        if (usingSingleStatusFilter) {
          showToast(
            SUCCESS,
            intl.formatMessage(
              {
                id: 'AutomationPage.resetStatusFiltersMessage2',
                defaultMessage:
                  'Because you have no {status} clauses, we reset your filters to show you all clauses.',
                description:
                  'Message when status filters are reset. Example: status = "draft"',
              },
              {
                status: toLowercaseTrimmed(
                  clause?.statusFilter?.[0] ?? 'active',
                ),
              },
            ),
          );

          dispatch(
            slices.automation.actions.setAutomationClause({
              ...clause,
              statusFilter: ALL_STATUSES,
            }),
          );
        } else {
          showToast(
            SUCCESS,
            intl.formatMessage({
              id: 'AutomationPage.resetQuickAiFiltersMessage',
              defaultMessage:
                'Because you have no Quick AI clauses, we reset your filters to show you all clauses.',
              description: 'Message when Quick AI filters are reset',
            }),
          );

          dispatch(
            slices.automation.actions.setAutomationClause({
              ...clause,
              typeFilter: ALL_TYPES,
            }),
          );
        }
      } else {
        showToast(
          SUCCESS,
          intl.formatMessage({
            id: 'AutomationPage.resetGuidedAiFiltersMessage',
            defaultMessage:
              'Because you have no Guided AI clauses, we reset your filters to show you all clauses.',
            description: 'Message when Guided AI filters are reset',
          }),
        );

        dispatch(
          slices.automation.actions.setAutomationClause({
            ...clause,
            typeFilter: ALL_TYPES,
          }),
        );
      }
    }
  }

  if (
    !hasAutomationSmartClauseFull &&
    !isFetching &&
    dataExists &&
    choiceHasNoData
  ) {
    if (clause?.statusFilter?.length <= 1) {
      showToast(
        SUCCESS,
        intl.formatMessage(
          {
            id: 'AutomationPage.resetStatusFiltersMessage3',
            defaultMessage:
              'Because you have no {status} clauses, we reset your filters to show you all clauses.',
            description:
              'Message when status filters are reset. Example: status = "inactive"',
          },
          {
            status: toLowercaseTrimmed(clause?.statusFilter?.[0] || ''),
          },
        ),
      );

      dispatch(
        slices.automation.actions.setAutomationClause({
          ...clause,
          statusFilter: ALL_STATUSES,
        }),
      );
    }
  }

  // TODO: Should be moved to their its own component.
  const clauseContent = (
    <Layout direction="column" spacing={8} mt={8}>
      {!hideClauseAffordance && !isFetchingAllClauses && showClauseLimit && (
        <Alert
          enableIcon
          variant={clauseVariant}
          onDismiss={() => setShowClauseLimit(false)}
        >
          <Text variant="tiny">
            <FormattedMessage
              id="AutomationPage.clauseLimitMessage"
              defaultMessage="{clausesLeftMessage}Your license allows you to create {clauseLimit} Clauses in total. Check out <link>Help Center</link> for more info."
              description="Message about clause limit and remaining clauses. Example: clauseLimit = '10', clausesLeftMessage = 'You have 3 Clauses left. '"
              values={{
                clauseLimit,
                clausesLeftMessage:
                  clausesLeft > 0
                    ? intl.formatMessage(
                        {
                          id: 'AutomationPage.clausesLeftMessage',
                          defaultMessage:
                            'You have {clausesLeft} {clausesLeft, plural, one {Clause} other {Clauses}} left. ',
                          description:
                            'Message showing remaining clauses. Example: clausesLeft = "3"',
                        },
                        {
                          clausesLeft,
                        },
                      )
                    : intl.formatMessage({
                        id: 'AutomationPage.clauseLimitReached',
                        defaultMessage: "You've reached your limit! ",
                        description: 'Message when clause limit is reached',
                      }),
                link: (chunks) => (
                  <a href={HELP_CENTER_LINK} target="_blank" rel="noreferrer">
                    {chunks}
                  </a>
                ),
              }}
            />
          </Text>
        </Alert>
      )}
      {/* eslint-disable-next-line react/jsx-pascal-case -- deprecating */}
      <Filters_DEPRECATED
        fields={fields}
        filters={filters}
        groups={[]}
        options={filtersOptions}
        onChange={onSetFilter}
      />
      <LoadingContainer
        isLoading={isFetching || isFetchingAllClauses || isResolvingUsers}
      >
        {!error &&
          (!!data ? (
            <Table
              name="automation-clauses"
              rowActions={rowActions}
              totalCount={data.count}
              columns={columns}
              data={data.results}
              state={pagination}
              onUpdate={(pagination) =>
                dispatch(
                  slices.automation.actions.setAutomationClausePagination(
                    pagination,
                  ),
                )
              }
              options={{
                enableManageColumns: false,
                enableExportXlsx: false,
                enableSelectRows: false,
              }}
            />
          ) : (
            <FlexLayout
              flexDirection="column"
              alignItems="center"
              mt={4}
              space={4}
            >
              <EmptyPage
                preset="new-clause"
                resolveAction={{
                  variant: 'primary',
                  icon: 'plus',
                  iconPosition: 'left',
                  text: newClauseText,
                  onClick: onCreateNewClause,
                }}
              />
            </FlexLayout>
          ))}
      </LoadingContainer>
      <AutomationHubManagement />
      {!!renameClauseValue && (
        <RenameClauseModal
          value={renameClauseValue}
          error={renameClauseError?.response?.data?.error}
          onClose={setRenameClauseValue}
          onSubmit={renameClause}
        />
      )}
    </Layout>
  );

  const noClauseData = (
    <>
      <LoadingContainer isLoading={isFetchingAllClauses}>
        <FlexLayout flexDirection="column" alignItems="center" mt={4} space={4}>
          <EmptyPage
            preset="new-clause"
            resolveAction={{
              variant: 'primary',
              icon: 'plus',
              iconPosition: 'left',
              text: newClauseText,
              onClick: onCreateNewClause,
            }}
          />
        </FlexLayout>
      </LoadingContainer>
      <AutomationHubManagement />
    </>
  );

  const actions = [
    ...(dataExists
      ? [
          {
            icon: 'plus',
            level: 'primary',
            text: newClauseText,
            onClick: onCreateNewClause,
          },
        ]
      : []),
  ];

  return (
    <PageLayout
      actions={actions}
      title={intl.formatMessage({
        id: 'AutomationPage.pageTitle',
        defaultMessage: 'Automation Hub',
        description: 'Page title for automation hub',
      })}
    >
      {dataExists ? clauseContent : noClauseData}
    </PageLayout>
  );
};

export default Page;
